export const getSsrUrlParams = (ctx) => {
    if (!ctx) return {};

    const url = ctx.url ?? ctx;

    if (!url.startsWith('/admin/')) {
        return {};
    }

    const urlParams = url.split('/').filter((value) => value);

    return {
        team: urlParams[1] || '',
        product: urlParams[2] || '',
    };
};

/**
 * Check if the user has access to the product
 *
 * @param {Object} user
 * @param {string} product
 * @return {boolean} true if the user has access to the product
 */
const checkProductManagementAccess = (user, product) => {
    if (!user) {
        return false;
    }

    return user.productAccess[product] || false;
};

/**
 * Get the manageable products from the user object
 *
 * @param {Object} user the user object from useAuth composable
 */
const getManageableProducts = (user) => {
    return Object.entries(user?.productAccess ?? {})
        .filter(([, value]) => value.canManage)
        .map(([key]) => key);
};

/**
 * Get the Team UUID from the user object
 * If the team is not provided, it will return the last used team or the current team
 * @param {Object} user the user object from useAuth composable
 * @param {string} [team] uuid of the team to be used
 * @returns {string|null} uuid of the team or null if not found
 */
const getTeam = (user, team) => {
    if (team !== null) {
        return team;
    }

    const _user = unref(user);

    if (!_user?.lastUsedTeam) {
        return _user.mainTeamUuid;
    }

    const lastUsedTeam = _user.lastUsedTeam;

    if (typeof lastUsedTeam === 'string') {
        return lastUsedTeam;
    }

    return _user?.lastUsedTeam?.uuid;
};

/**
 * Get the Product UUID from the user object
 * If the product is not provided, it will return the last used product or the first manageable product
 * @param {Object} user the user object from useAuth composable
 * @param {string} [product] uuid of the product to be used
 * @returns {string|null} uuid of the product or null if not found
 */
const getProduct = (user, product) => {
    if (product !== null) {
        return product;
    }
    const _user = unref(user);
    const manageableProducts = getManageableProducts(_user);

    const lastUsedProduct =
        _user.lastUsedProduct?.uuid ?? _user.lastUsedProduct;

    if (!lastUsedProduct) {
        return manageableProducts[0] || null;
    }

    return checkProductManagementAccess(_user, lastUsedProduct ?? '')
        ? lastUsedProduct
        : manageableProducts[0];
};

/**
 * Get the admin base url from the user object
 *
 * @param {Object} user the user object from useAuth composable
 * @param {string} [team] uuid of the team to be used
 * @param {string} [product] uuid of the product to be used
 * @returns url to the admin dashboard
 */
export const getAdminBaseUrl = (user, team = null, product = null) => {
    const _user = unref(user);

    const teamUuid = getTeam(_user, team);
    const productUuid = getProduct(_user, product);

    return `/admin/${teamUuid}/${productUuid}`;
};

/**
 * Get the admin dashboard url from the user object
 *
 * @param {Object} user the user object from useAuth composable
 * @param {string} [team] uuid of the team to be used
 * @param {string} [product] uuid of the product to be used
 * @returns url to the admin dashboard
 */
export const getAdminDashboardFromProfile = (
    user,
    team = null,
    product = null,
) => {
    const baseUrl = getAdminBaseUrl(user, team, product);

    return `${baseUrl}/dashboard`;
};

export const redirectToAdminDashboard = (user, team, product) => {
    const localePath = useLocalePath();

    const url = getAdminDashboardFromProfile(user, team, product);

    return localePath(url);
};
